import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSSE } from "../hooks/useSSE";
import {
  ArrowLeft, Plus, Trash2, Edit2, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, Search, FileText, AlertTriangle,
  CreditCard, BookOpen, Filter, Printer, TrendingDown, Loader2
} from "lucide-react";
import { AuthContext } from "../App";
import { toast } from "sonner";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hoyStr = () => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);

const fmtNum = (n) =>
  n != null ? Number(n).toLocaleString("es-PY") : "—";

const ESTADO_BADGE = {
  disponible:  "bg-slate-700 text-slate-300",
  emitido:     "bg-emerald-900/60 text-emerald-300",
  diferido:    "bg-amber-900/60 text-amber-300",
  descontado:  "bg-cyan-900/60 text-cyan-300",
  anulado:     "bg-red-900/60 text-red-300",
};
const ESTADO_LABEL = {
  disponible:  "Disponible",
  emitido:     "Emitido",
  diferido:    "Diferido",
  descontado:  "Descontado",
  anulado:     "Anulado",
};

// embedded=true → se usa dentro de FinancieroPage (sin header propio ni min-h-screen)
export default function ChequesPage({ embedded = false }) {
  const { token, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const navigate = useNavigate();

  const logoTipo = activeEmpresaPropia?.slug || "";
  const puedeVer            = hasPermission("cheques.ver");
  const puedeRegistrar      = hasPermission("cheques.registrar_chequera");
  const puedeEditar         = hasPermission("cheques.editar_chequera");
  const puedeEmitir         = hasPermission("cheques.emitir");
  const puedeDescontar      = hasPermission("cheques.descontar");
  const puedeAnular         = hasPermission("cheques.anular");
  const puedeLiberar        = hasPermission("cheques.liberar");
  const puedeEliminar       = hasPermission("cheques.eliminar_chequera");
  const puedeConfigurarImpresion = hasPermission("cheques.configurar_impresion");

  const [tab, setTab] = useState("talonarios");

  // ── Bancos ──────────────────────────────────────────
  const [bancos, setBancos] = useState([]);

  // ── Talonarios ──────────────────────────────────────
  const [chequeras, setChequeras] = useState([]);
  const [loadingCh, setLoadingCh] = useState(false);
  const [showFormCh, setShowFormCh] = useState(false);
  const [editingCh, setEditingCh] = useState(null);
  const [formCh, setFormCh] = useState({
    cuenta_bancaria_id: "", serie: "", desde: "", hasta: "", fecha_activacion: ""
  });
  const [savingCh, setSavingCh] = useState(false);

  // ── SSE — eventos en tiempo real ─────────────────────────────────────────
  useSSE({
    logoTipo,
    token,
    enabled: !!logoTipo && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo === "cheque_actualizado") {
        setHojas(prev => prev.map(h => h.id === event.data.id ? { ...h, estado: event.data.estado } : h));
      }
    }, []), // eslint-disable-line
  });

  // ── Auditoría (hojas) ────────────────────────────────
  const [hojas, setHojas] = useState([]);
  const [totalHojas, setTotalHojas] = useState(0);
  const [pageHojas, setPageHojas] = useState(1);
  const [pagesHojas, setPagesHojas] = useState(1);
  const [loadingH, setLoadingH] = useState(false);
  const [filtros, setFiltros] = useState({
    chequera_id: "", estado: "", cuenta_bancaria_id: "", desde_fecha: hoyStr(), hasta_fecha: hoyStr()
  });

  // ── Modal emitir ─────────────────────────────────────
  const [hojaEmitiendo, setHojaEmitiendo] = useState(null);
  const [formEmitir, setFormEmitir] = useState({
    nombre_beneficiario: "", ruc_beneficiario: "", valor: "",
    fecha_emision: "", fecha_pago: "", concepto: ""
  });
  const [savingEmitir, setSavingEmitir] = useState(false);
  const [comprasPend, setComprasPend] = useState([]);
  // Aviso de cheques diferidos que ya llegaron a su fecha de pago
  const [diferidosVencidos, setDiferidosVencidos] = useState([]);
  const [avisoDiferidosAbierto, setAvisoDiferidosAbierto] = useState(false);
  // Vincular un cheque ya emitido (suelto) a una compra pendiente
  const [hojaVinculando, setHojaVinculando] = useState(null);
  const [compraVincHoja, setCompraVincHoja] = useState("");
  const [tcVincHoja, setTcVincHoja] = useState("");
  const [savingVinc, setSavingVinc] = useState(false);
  const [compraVincId, setCompraVincId] = useState("");
  const [tcEmitir, setTcEmitir] = useState("");

  // ── Modal anular ─────────────────────────────────────
  const [hojaAnulando, setHojaAnulando] = useState(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  const [savingAnular, setSavingAnular] = useState(false);

  // ── Modal descontar ───────────────────────────────────
  const [hojaDescontando, setHojaDescontando] = useState(null);
  const [fechaDescuento, setFechaDescuento] = useState("");
  const [savingDescontar, setSavingDescontar] = useState(false);

  // ── Proyección ────────────────────────────────────────
  const [proyData, setProyData] = useState(null);
  const [loadingProy, setLoadingProy] = useState(false);

  // ── Configuración de impresión (medidas personalizables) ─────────────
  const DEFAULT_VISTA_IMPRESION = {
    monto_top: 3, monto_right: 3,
    fecha_top: 11, fecha_right: 5,
    benef_top: 18, benef_left: 4,
    letras_top: 25, letras_left: 4,
    concepto_top: 32, concepto_left: 4,
  };
  const DEFAULT_DIFERIDO_IMPRESION = {
    fecha_emision_top: 13, fecha_emision_left: 5,
    fecha_pago_top: 13, fecha_pago_right: 5,
    benef_top: 22, benef_left: 4,
    letras_top: 29, letras_left: 4,
    concepto_top: 36, concepto_left: 4,
  };
  const [configScope, setConfigScope] = useState("general"); // "general" | cuenta_bancaria_id
  const [configList, setConfigList] = useState([]);
  const [configForms, setConfigForms] = useState({ vista: { ...DEFAULT_VISTA_IMPRESION }, diferido: { ...DEFAULT_DIFERIDO_IMPRESION } });
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // ── Carga inicial ────────────────────────────────────
  const cargarBancos = useCallback(async () => {
    const res = await fetch(`${API}/admin/cheques/bancos?logo_tipo=${logoTipo}`, { headers: authHeaders, cache: "no-store" });
    if (res.ok) setBancos(await res.json());
  }, [logoTipo, token]);

  const cargarChequeras = useCallback(async () => {
    setLoadingCh(true);
    try {
      // cache: "no-store" — sin esto, el navegador (o un proxy/CDN delante
      // del dominio) podía servir una respuesta anterior de este mismo GET
      // y el talonario recién creado no aparecía ni apretando "Actualizar".
      const res = await fetch(`${API}/admin/cheques/chequeras?logo_tipo=${logoTipo}`, { headers: authHeaders, cache: "no-store" });
      if (res.ok) {
        setChequeras(await res.json());
      } else {
        // Antes, si esta consulta fallaba (ej. lentitud momentánea justo
        // después de crear un talonario grande) se quedaba sin avisar nada,
        // dejando la tabla tal cual estaba — vacía si era el primer
        // talonario de la empresa — y el usuario creía que no se había
        // guardado, cuando sí. Ahora se avisa para que sepa que debe
        // reintentar (con el botón Actualizar) en vez de registrar de nuevo.
        toast.error("No se pudo actualizar la lista de talonarios. Probá tocar \"Actualizar\".");
      }
    } finally {
      setLoadingCh(false);
    }
  }, [logoTipo, token]);

  const cargarHojas = useCallback(async (pg = 1) => {
    setLoadingH(true);
    try {
      const params = new URLSearchParams({ logo_tipo: logoTipo, page: pg, limit: 50 });
      if (filtros.chequera_id)        params.set("chequera_id", filtros.chequera_id);
      if (filtros.estado)             params.set("estado", filtros.estado);
      if (filtros.cuenta_bancaria_id) params.set("cuenta_bancaria_id", filtros.cuenta_bancaria_id);
      if (filtros.desde_fecha)        params.set("desde_fecha", filtros.desde_fecha);
      if (filtros.hasta_fecha)        params.set("hasta_fecha", filtros.hasta_fecha);
      const res = await fetch(`${API}/admin/cheques/hojas?${params}`, { headers: authHeaders, cache: "no-store" });
      if (res.ok) {
        const d = await res.json();
        setHojas(d.hojas);
        setTotalHojas(d.total);
        setPagesHojas(d.pages);
        setPageHojas(d.page);
      }
    } finally {
      setLoadingH(false);
    }
  }, [logoTipo, token, filtros]);

  useEffect(() => {
    if (!puedeVer) return;
    cargarBancos();
    cargarChequeras();
  }, [logoTipo]);

  // Al entrar: avisar si hay cheques diferidos cuya fecha de pago ya llegó
  useEffect(() => {
    if (!puedeVer || !logoTipo || !token) return;
    (async () => {
      try {
        const r = await fetch(`${API}/admin/cheques/proyeccion?logo_tipo=${logoTipo}`, { headers: authHeaders, cache: "no-store" });
        if (!r.ok) return;
        const d = await r.json();
        const hoyStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
        const vencidos = (d.diferidos || []).filter(h => (h.fecha_pago || "9999") <= hoyStr);
        setDiferidosVencidos(vencidos);
        setAvisoDiferidosAbierto(vencidos.length > 0);
      } catch { /* aviso opcional */ }
    })();
  }, [logoTipo, token, puedeVer]);

  const liberarHoja = async (hoja) => {
    if (!window.confirm(`¿Liberar el cheque ${hoja.serie}${String(hoja.numero).padStart(6, "0")}? Volverá a quedar disponible (usalo solo si no se entregó).`)) return;
    try {
      const res = await fetch(`${API}/admin/cheques/hojas/${hoja.id}/revertir-emision`, {
        method: "PATCH", headers: authHeaders, body: JSON.stringify({ logo_tipo: logoTipo }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(typeof data.detail === "string" ? data.detail : "No se pudo liberar"); return; }
      toast.success("Cheque liberado: vuelve a estar disponible");
      cargarHojas(pageHojas);
      cargarChequeras();
    } catch { toast.error("Error al liberar el cheque"); }
  };

  // Liberar cheque YA USADO (emitido/diferido con pago, o descontado): vuelve a disponible
  // y se desvincula del pago (gasto/compra/pago proveedor) para poder anular ese pago.
  const liberarChequeUsado = async (hoja) => {
    const cod = `${hoja.serie}${String(hoja.numero).padStart(6, "0")}`;
    const motivo = window.prompt(
      `Liberar el cheque ${cod} (${hoja.estado}).\n\nVuelve a quedar DISPONIBLE y se desvincula del pago asociado (el pago NO se anula: después podés anularlo o volver a pagar).\n\nMotivo (mín. 6 caracteres):`
    );
    if (motivo === null) return;
    if (motivo.trim().length < 6) return toast.error("El motivo debe tener al menos 6 caracteres");
    try {
      const res = await fetch(`${API}/admin/cheques/hojas/${hoja.id}/liberar`, {
        method: "PATCH", headers: authHeaders, body: JSON.stringify({ motivo: motivo.trim(), logo_tipo: logoTipo }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(typeof data.detail === "string" ? data.detail : (data.detail?.[0]?.msg || "No se pudo liberar")); return; }
      const n = (data.pagos_desvinculados || []).length;
      toast.success(n ? `Cheque ${cod} liberado. ${n} pago(s) desvinculado(s): ya podés anularlos.` : `Cheque ${cod} liberado: vuelve a estar disponible`);
      cargarHojas(pageHojas);
      cargarChequeras();
    } catch { toast.error("Error al liberar el cheque"); }
  };

  useEffect(() => {
    if (tab === "auditoria" && puedeVer) cargarHojas(1);
    if (tab === "proyeccion" && puedeVer) cargarProyeccion();
    if (tab === "configuracion" && puedeConfigurarImpresion) cargarConfigImpresionLista();
  }, [tab]);

  const cargarProyeccion = async () => {
    setLoadingProy(true);
    try {
      const params = logoTipo ? `?logo_tipo=${logoTipo}` : "";
      const res = await fetch(`${API}/admin/cheques/proyeccion${params}`, { headers: authHeaders, cache: "no-store" });
      if (res.ok) setProyData(await res.json());
    } catch {}
    setLoadingProy(false);
  };

  // ── Configuración de impresión ────────────────────────
  const cargarConfigImpresionLista = useCallback(async () => {
    if (!logoTipo) return;
    setLoadingConfig(true);
    try {
      const res = await fetch(`${API}/admin/cheques/config-impresion/lista?logo_tipo=${logoTipo}`, { headers: authHeaders, cache: "no-store" });
      if (res.ok) setConfigList(await res.json());
    } catch {}
    setLoadingConfig(false);
  }, [logoTipo, token]);

  // Al cambiar el alcance (general o una cuenta puntual) o al recargar la lista,
  // volcamos al formulario los valores guardados para ese alcance (o los
  // defaults si todavía no se personalizó nada ahí).
  useEffect(() => {
    if (tab !== "configuracion") return;
    const cid = configScope === "general" ? null : configScope;
    const match = configList.find(c => (c.cuenta_bancaria_id || null) === cid);
    setConfigForms({
      vista: { ...DEFAULT_VISTA_IMPRESION, ...(match?.vista || {}) },
      diferido: { ...DEFAULT_DIFERIDO_IMPRESION, ...(match?.diferido || {}) },
    });
  }, [tab, configScope, configList]); // eslint-disable-line

  const setCampoImpresion = (tipo, campo, val) => {
    setConfigForms(p => ({ ...p, [tipo]: { ...p[tipo], [campo]: val === "" ? "" : parseFloat(val) } }));
  };

  const handleGuardarConfigImpresion = async () => {
    setSavingConfig(true);
    try {
      const body = {
        logo_tipo: logoTipo,
        cuenta_bancaria_id: configScope === "general" ? null : configScope,
        vista: configForms.vista,
        diferido: configForms.diferido,
      };
      const res = await fetch(`${API}/admin/cheques/config-impresion`, { method: "PUT", headers: authHeaders, body: JSON.stringify(body) });
      if (res.ok) {
        toast.success("Configuración de impresión guardada");
        cargarConfigImpresionLista();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo guardar la configuración");
      }
    } finally {
      setSavingConfig(false);
    }
  };

  const handleRestaurarConfigImpresion = async () => {
    const cid = configScope === "general" ? null : configScope;
    const match = configList.find(c => (c.cuenta_bancaria_id || null) === cid);
    if (!match) {
      setConfigForms({ vista: { ...DEFAULT_VISTA_IMPRESION }, diferido: { ...DEFAULT_DIFERIDO_IMPRESION } });
      return;
    }
    if (!window.confirm("¿Restaurar los valores por defecto para este alcance?")) return;
    const res = await fetch(`${API}/admin/cheques/config-impresion/${match.id}`, { method: "DELETE", headers: authHeaders });
    if (res.ok) {
      toast.success("Restaurado a los valores por defecto");
      cargarConfigImpresionLista();
    } else {
      toast.error("No se pudo restaurar");
    }
  };

  // ── Talonarios — guardar ─────────────────────────────
  const handleGuardarCh = async (e) => {
    e.preventDefault();
    if (!formCh.cuenta_bancaria_id) return toast.error("Seleccioná una cuenta bancaria");
    if (!formCh.serie.trim())       return toast.error("Ingresá la serie");
    if (!formCh.fecha_activacion)   return toast.error("Ingresá la fecha de activación");

    let body;
    if (editingCh) {
      body = {
        cuenta_bancaria_id: formCh.cuenta_bancaria_id,
        serie: formCh.serie,
        fecha_activacion: formCh.fecha_activacion,
      };
    } else {
      const desde = Number(formCh.desde);
      const hasta = Number(formCh.hasta);
      if (!desde || desde < 1) return toast.error("Ingresá el número desde (mayor a 0)");
      if (!hasta || hasta < 1) return toast.error("Ingresá el número hasta (mayor a 0)");
      if (hasta < desde) return toast.error("'Hasta' debe ser mayor o igual a 'Desde'");
      if ((hasta - desde + 1) > 1000) return toast.error("El rango no puede superar 1000 hojas");
      body = { cuenta_bancaria_id: formCh.cuenta_bancaria_id, serie: formCh.serie, desde, hasta, fecha_activacion: formCh.fecha_activacion, logo_tipo: logoTipo };
    }

    setSavingCh(true);
    try {
      const url = editingCh
        ? `${API}/admin/cheques/chequeras/${editingCh.id}`
        : `${API}/admin/cheques/chequeras`;
      const method = editingCh ? "PATCH" : "POST";
      const res = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data.detail === "string" ? data.detail : (data.detail?.[0]?.msg || "Error al guardar");
        return toast.error(msg);
      }
      toast.success(editingCh ? "Talonario actualizado" : `Talonario creado — ${data.hojas_creadas} hojas generadas`);
      setShowFormCh(false);
      setEditingCh(null);
      setFormCh({ cuenta_bancaria_id: "", serie: "", desde: "", hasta: "", fecha_activacion: "" });
      cargarChequeras();
    } finally {
      setSavingCh(false);
    }
  };

  const abrirEditar = (ch) => {
    setEditingCh(ch);
    setFormCh({
      cuenta_bancaria_id: ch.cuenta_bancaria_id,
      serie: ch.serie,
      desde: ch.desde,
      hasta: ch.hasta,
      fecha_activacion: ch.fecha_activacion,
    });
    setShowFormCh(true);
  };

  const handleEliminarCh = async (ch) => {
    if (!window.confirm(`¿Eliminar talonario serie ${ch.serie} (${ch.desde}-${ch.hasta})? Se eliminarán todas las hojas disponibles.`)) return;
    const res = await fetch(`${API}/admin/cheques/chequeras/${ch.id}`, { method: "DELETE", headers: authHeaders });
    const data = await res.json();
    if (!res.ok) {
      const msg = typeof data.detail === "string" ? data.detail : (data.detail?.[0]?.msg || "No se puede eliminar");
      return toast.error(msg);
    }
    toast.success("Talonario eliminado");
    cargarChequeras();
  };

  // ── Emitir ───────────────────────────────────────────
  const abrirEmitir = (hoja) => {
    const hoy = new Date().toISOString().slice(0, 10);
    setHojaEmitiendo(hoja);
    setFormEmitir({ nombre_beneficiario: "", ruc_beneficiario: "", valor: "", fecha_emision: hoy, fecha_pago: hoy, concepto: "" });
    setCompraVincId("");
    setTcEmitir("");
    setComprasPend([]);
    // Compras de contado con saldo pendiente (para vincular el cheque y registrar el pago)
    (async () => {
      try {
        const acc = [];
        for (const est of ["pendiente", "parcial", "vencido"]) {
          const r = await fetch(`${API}/admin/compras?estado_pago=${est}&logo_tipo=${logoTipo}`, { headers: authHeaders, cache: "no-store" });
          if (r.ok) acc.push(...(await r.json()));
        }
        setComprasPend(acc.filter(c => c.tipo_pago !== "credito" && !c.solo_iva && Number(c.saldo_pendiente) > 0));
      } catch { /* opcional */ }
    })();
  };
  const compraVinc = comprasPend.find(c => c.id === compraVincId) || null;
  const elegirCompraVinc = (id) => {
    setCompraVincId(id);
    const c = comprasPend.find(x => x.id === id);
    if (c) {
      setFormEmitir(p => ({
        ...p,
        nombre_beneficiario: c.proveedor_nombre || p.nombre_beneficiario,
        ruc_beneficiario: c.proveedor_ruc || p.ruc_beneficiario,
        valor: String(Number(c.saldo_pendiente) || 0),
        concepto: p.concepto || `Compra ${c.numero || ""}`.trim(),
      }));
    }
  };

  const handleEmitir = async (e) => {
    e.preventDefault();
    if (!formEmitir.nombre_beneficiario.trim()) return toast.error("Ingresá el beneficiario");
    if (!formEmitir.valor || Number(formEmitir.valor) <= 0) return toast.error("Ingresá un valor válido");
    if (!formEmitir.fecha_emision) return toast.error("Ingresá la fecha de emisión");
    if (!formEmitir.fecha_pago)    return toast.error("Ingresá la fecha de pago");
    if (formEmitir.fecha_pago < formEmitir.fecha_emision) return toast.error("La fecha de pago no puede ser anterior a la de emisión");

    setSavingEmitir(true);
    try {
      const res = await fetch(`${API}/admin/cheques/hojas/${hojaEmitiendo.id}/emitir`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({
          ...formEmitir, valor: Number(formEmitir.valor), logo_tipo: logoTipo,
          ...(compraVinc ? { egreso_tipo: "compra", egreso_id: compraVinc.id, registrar_pago: true, tipo_cambio: tcEmitir ? Number(tcEmitir) : null } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data.detail === "string" ? data.detail : (data.detail?.[0]?.msg || "Error al emitir");
        toast.error(msg);
        return; // mantener modal abierto para que el usuario pueda reintentar
      }
      const tipo = data.hoja.tipo_cheque === "diferido" ? "Diferido" : "Al día";
      toast.success(data.pago_registrado ? `Cheque emitido (${tipo}) y pago registrado en la compra` : `Cheque emitido (${tipo})`);
      setHojaEmitiendo(null);
      cargarHojas(pageHojas);
      cargarChequeras();
    } finally {
      setSavingEmitir(false);
    }
  };

  // ── Anular ───────────────────────────────────────────
  const handleAnular = async (e) => {
    e.preventDefault();
    if (motivoAnulacion.trim().length < 6) return toast.error("El motivo debe tener al menos 6 caracteres");
    setSavingAnular(true);
    try {
      const res = await fetch(`${API}/admin/cheques/hojas/${hojaAnulando.id}/anular`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({ motivo: motivoAnulacion }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data.detail === "string" ? data.detail : (data.detail?.[0]?.msg || "Error al anular");
        toast.error(msg);
        setHojaAnulando(null);
        cargarHojas(pageHojas);
        return;
      }
      toast.success("Cheque anulado");
      setHojaAnulando(null);
      setMotivoAnulacion("");
      cargarHojas(pageHojas);
      cargarChequeras();
    } finally {
      setSavingAnular(false);
    }
  };

  // ── Descontar ────────────────────────────────────────
  const abrirDescontar = (hoja) => {
    const hoy = new Date().toISOString().slice(0, 10);
    setHojaDescontando(hoja);
    setFechaDescuento(hoy);
  };

  const handleDescontar = async (e) => {
    e.preventDefault();
    if (!fechaDescuento) return toast.error("Ingresá la fecha de descuento");
    setSavingDescontar(true);
    try {
      const res = await fetch(`${API}/admin/cheques/hojas/${hojaDescontando.id}/descontar`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({ fecha_descuento: fechaDescuento, logo_tipo: logoTipo }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = typeof data.detail === "string" ? data.detail : (data.detail?.[0]?.msg || "Error al descontar");
        toast.error(msg);
        setHojaDescontando(null);
        cargarHojas(pageHojas);
        return;
      }
      toast.success("Cheque marcado como descontado — aparecerá en el reporte caja/banco");
      setDiferidosVencidos(prev => prev.filter(x => x.id !== hojaDescontando.id));
      setHojaDescontando(null);
      cargarHojas(pageHojas);
      cargarChequeras();
    } finally {
      setSavingDescontar(false);
    }
  };

  // ── Imprimir cheque — texto overlay para hoja pre-impresa del banco ─────────
  const imprimirCheque = async (hoja) => {
    const w = window.open("", "_blank");
    if (!w) return;

    // Config de medidas: la de la cuenta puntual si existe, si no la general
    // de la empresa, si no los valores por defecto de siempre (fallback ya
    // resuelto por el backend en /admin/cheques/config-impresion).
    let cfgVista = DEFAULT_VISTA_IMPRESION;
    let cfgDiferido = DEFAULT_DIFERIDO_IMPRESION;
    try {
      const q = new URLSearchParams();
      if (logoTipo) q.set("logo_tipo", logoTipo);
      if (hoja.cuenta_bancaria_id) q.set("cuenta_bancaria_id", hoja.cuenta_bancaria_id);
      const resCfg = await fetch(`${API}/admin/cheques/config-impresion?${q}`, { headers: authHeaders, cache: "no-store" });
      if (resCfg.ok) {
        const dataCfg = await resCfg.json();
        cfgVista = { ...DEFAULT_VISTA_IMPRESION, ...(dataCfg.vista || {}) };
        cfgDiferido = { ...DEFAULT_DIFERIDO_IMPRESION, ...(dataCfg.diferido || {}) };
      }
    } catch {}

    const moneda      = hoja.moneda || "PYG";
    const esDiferido  = hoja.tipo_cheque === "diferido";
    const esAnulado   = hoja.estado === "anulado";
    const simbolo     = moneda === "USD" ? "USD" : moneda === "BRL" ? "R$" : "₲";
    const monedaStr   = moneda === "USD" ? "DÓLARES AMERICANOS" : moneda === "BRL" ? "REALES BRASILEÑOS" : "GUARANÍES";
    const chequeNum   = String(hoja.numero).padStart(6, "0");

    const fmtF = (s) => {
      if (!s) return "__ - __ - ____";
      const [y, m, d] = s.split("-");
      return `${d} - ${m} - ${y}`;
    };

    const numToWords = (num) => {
      num = Math.round(num);
      if (num === 0) return "CERO";
      const U = ["","UN","DOS","TRES","CUATRO","CINCO","SEIS","SIETE","OCHO","NUEVE",
                 "DIEZ","ONCE","DOCE","TRECE","CATORCE","QUINCE","DIECISÉIS","DIECISIETE","DIECIOCHO","DIECINUEVE","VEINTE"];
      const D = ["","","VEINTE","TREINTA","CUARENTA","CINCUENTA","SESENTA","SETENTA","OCHENTA","NOVENTA"];
      const C = ["","CIENTO","DOSCIENTOS","TRESCIENTOS","CUATROCIENTOS","QUINIENTOS","SEISCIENTOS","SETECIENTOS","OCHOCIENTOS","NOVECIENTOS"];
      const menorMil = (n) => {
        if (n === 0) return "";
        if (n === 100) return "CIEN";
        let r = "";
        const c = Math.floor(n / 100), re = n % 100;
        if (c) r += C[c] + (re ? " " : "");
        if (re <= 20) r += U[re];
        else { const dg = Math.floor(re / 10), u = re % 10; r += D[dg] + (u ? " Y " + U[u] : ""); }
        return r;
      };
      const mm = Math.floor(num / 1_000_000), mi = Math.floor((num % 1_000_000) / 1000), un = num % 1000;
      const parts = [];
      if (mm === 1) parts.push("UN MILLÓN"); else if (mm > 1) parts.push(menorMil(mm) + " MILLONES");
      if (mi === 1) parts.push("MIL"); else if (mi > 1) parts.push(menorMil(mi) + " MIL");
      if (un > 0 || parts.length === 0) parts.push(menorMil(un) || "CERO");
      return parts.join(" ");
    };

    const montoStr    = hoja.valor != null ? fmtNum(hoja.valor) : "";
    const montoLetras = hoja.valor != null ? numToWords(hoja.valor) : "";

    // Print overlay — solo texto para completar la hoja pre-impresa del banco.
    // Sin decoración: el papel físico del banco ya trae banco, cuenta, CMC-7, etc.
    w.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Cheque ${hoja.serie}-${chequeNum}</title>
<style>
  @page { size: 175mm 76mm; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; background: #fff; color: #000; }
  .cheque { position: relative; width: 175mm; height: 76mm; overflow: hidden; }
  ${esAnulado ? `.cheque::after { content: "ANULADO"; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%) rotate(-20deg); font-size: 30pt; font-weight: bold; color: rgba(200,0,0,0.15); pointer-events: none; letter-spacing: 6px; }` : ""}

  /*
   * Layout según especificación BCPY:
   *   Total: 175mm × 76mm
   *   Header (logo/cuenta/serie): ~8mm (vista) / ~12mm (diferido)
   *   Firma izq: 85mm × 18mm  |  Firma der: 80mm × 20mm
   *   Franja MICR inferior: 16mm
   *
   * CHEQUE VISTA  — campos de texto overlay:
   *   Monto N°: top 5mm, far right (caja 3mm × 7mm)
   *   Fecha emisión: top ~11mm, right
   *   Beneficiario:  top ~18mm
   *   La suma de:    top ~25mm
   *   Concepto:      top ~32mm
   *
   * CHEQUE DIFERIDO — igual pero header +4mm → todo desplazado:
   *   Fecha emisión: top ~13mm, izquierda
   *   Fecha pago:    top ~13mm, derecha (misma línea)
   *   Beneficiario:  top ~22mm
   *   La suma de:    top ~29mm
   *   Concepto:      top ~36mm
   */

  /* ── Monto numérico (caja top-right del banco) ────── */
  .monto-num { position: absolute; top: ${cfgVista.monto_top}mm; right: ${cfgVista.monto_right}mm; font-size: 9pt; font-weight: 700; font-family: 'Courier New', monospace; letter-spacing: 1px; }

  /* ── Fecha de emisión ────────────────────────────────────────── */
  .fecha-row { position: absolute; top: ${cfgVista.fecha_top}mm; right: ${cfgVista.fecha_right}mm; display: flex; gap: 4mm; align-items: baseline; }
  .fecha-val { font-size: 9pt; font-weight: 700; font-family: 'Courier New', monospace; letter-spacing: 2px; }
  .fecha-sep { font-size: 8pt; color: #555; margin: 0 0.5mm; }

  /* ── Beneficiario ────────────────────────────────────────────── */
  .benef-row { position: absolute; top: ${cfgVista.benef_top}mm; left: ${cfgVista.benef_left}mm; right: 4mm; display: flex; align-items: baseline; gap: 2mm; }
  .benef-val { font-size: 9pt; font-weight: 700; flex: 1; white-space: nowrap; overflow: hidden; }
  .ruc-val   { font-size: 7.5pt; font-weight: 600; white-space: nowrap; }

  /* ── Monto en letras ─────────────────────────────────────────── */
  .letras-row { position: absolute; top: ${cfgVista.letras_top}mm; left: ${cfgVista.letras_left}mm; right: 4mm; display: flex; align-items: baseline; gap: 2mm; }
  .letras-monto { font-size: 8pt; font-weight: 700; white-space: nowrap; }
  .letras-txt   { font-size: 8pt; font-weight: 700; overflow: hidden; white-space: nowrap; }

  /* ── Concepto ────────────────────────────────────────────────── */
  .concepto-row { position: absolute; top: ${cfgVista.concepto_top}mm; left: ${cfgVista.concepto_left}mm; right: 4mm; }
  .concepto-val { font-size: 7.5pt; font-weight: 600; }

  /* ── Diferido: emisión izq / pago der en la misma línea ── */
  .diferido .fecha-row     { top: ${cfgDiferido.fecha_emision_top}mm; right: auto; left: ${cfgDiferido.fecha_emision_left}mm; }
  .fecha-pago-row { position: absolute; top: ${cfgDiferido.fecha_pago_top}mm; right: ${cfgDiferido.fecha_pago_right}mm; display: flex; align-items: baseline; gap: 2mm; }
  .fecha-pago-lbl { font-size: 7pt; color: #444; font-weight: 600; text-transform: uppercase; }
  .fecha-pago-val { font-size: 9pt; font-weight: 700; font-family: 'Courier New', monospace; letter-spacing: 1px; }
  /* Diferido: desplazar campos de escritura (header más alto) */
  .diferido .benef-row    { top: ${cfgDiferido.benef_top}mm; left: ${cfgDiferido.benef_left}mm; }
  .diferido .letras-row   { top: ${cfgDiferido.letras_top}mm; left: ${cfgDiferido.letras_left}mm; }
  .diferido .concepto-row { top: ${cfgDiferido.concepto_top}mm; left: ${cfgDiferido.concepto_left}mm; }

  /* Toolbar (no se imprime) */
  @media print { .toolbar { display: none !important; } body { background: #fff; } }
  .toolbar { background: #1e293b; color: #fff; padding: 8px 16px; display: flex; gap: 10px; align-items: center; font-family: Arial, sans-serif; font-size: 12px; }
  .toolbar button { background: #fff; color: #1e293b; border: none; padding: 5px 14px; border-radius: 4px; cursor: pointer; font-weight: bold; font-size: 12px; }
  .toolbar .ref { opacity: 0.7; font-size: 11px; }
  .wrap { display: flex; justify-content: center; align-items: center; padding: 16px; background: #f1f5f9; min-height: calc(100vh - 40px); }
  @media print { .wrap { padding: 0; background: #fff; min-height: unset; } }
  .preview-hint { font-size: 9px; color: #94a3b8; margin-top: 6px; text-align: center; font-style: italic; }
  @media print { .preview-hint { display: none; } }
</style>
</head>
<body>
<div class="toolbar">
  <button onclick="window.print()">🖨 Imprimir sobre cheque físico</button>
  <span class="ref">Cheque ${hoja.serie}-${chequeNum} &nbsp;·&nbsp; ${esDiferido ? "DIFERIDO" : "AL DÍA"} &nbsp;·&nbsp; ${simbolo} ${montoStr}</span>
</div>
<div class="wrap">
  <div>
    <div class="cheque${esDiferido ? " diferido" : ""}">

      <!-- Monto numérico (esquina superior derecha del cheque) -->
      <div class="monto-num">${simbolo} ${montoStr}</div>

      <!-- Fecha de emisión -->
      <div class="fecha-row">
        <span class="fecha-val">${fmtF(hoja.fecha_emision)}</span>
      </div>

      ${esDiferido && hoja.fecha_pago ? `
      <!-- Fecha de pago — misma línea que emisión, posicionada a la derecha -->
      <div class="fecha-pago-row">
        <span class="fecha-pago-lbl">Fecha de pago:</span>
        <span class="fecha-pago-val">${fmtF(hoja.fecha_pago)}</span>
      </div>` : ""}

      <!-- Beneficiario -->
      <div class="benef-row">
        <span class="benef-val">${hoja.nombre_beneficiario || ""}</span>
        ${hoja.ruc_beneficiario ? `<span class="ruc-val">&nbsp;&nbsp;C.I./R.U.C.: ${hoja.ruc_beneficiario}</span>` : ""}
      </div>

      <!-- Monto en letras -->
      <div class="letras-row">
        <span class="letras-monto">${simbolo} ${montoStr}</span>
        <span class="letras-txt">${montoLetras} ${monedaStr}</span>
      </div>

      <!-- Concepto -->
      <div class="concepto-row">
        <span class="concepto-val">${hoja.concepto || ""}</span>
      </div>

    </div>
    <div class="preview-hint">Posicionar el papel del cheque físico antes de imprimir · Ajustar márgenes según banco</div>
  </div>
</div>
</body>
</html>`);

    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  // ── ACCESS GUARD ─────────────────────────────────────
  if (!puedeVer) {
    return (
      <div className="py-16 text-center text-slate-500">
        <XCircle className="w-10 h-10 mx-auto mb-3 text-red-400 opacity-60" />
        <p>Sin permiso para ver cheques</p>
      </div>
    );
  }

  // ── CONTENIDO PRINCIPAL (compartido standalone/embedded) ───────────────────
  const tabsNav = (
    <div className={embedded ? "border-b border-white/10 mb-5" : "border-b border-white/10 px-6 mb-0"}>
      <div className="flex gap-1">
        {[
          { id: "talonarios",  label: "Talonarios",         icon: BookOpen },
          { id: "auditoria",   label: "Auditoría de Hojas", icon: FileText },
          { id: "proyeccion",  label: "Proyección",         icon: TrendingDown },
          ...(puedeConfigurarImpresion ? [{ id: "configuracion", label: "Configuración", icon: Filter }] : []),
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? "border-cyan-400 text-cyan-400"
                  : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const content = (
    <div className={embedded ? "space-y-5" : "p-6 max-w-7xl mx-auto space-y-6"}>

      {/* ═══════════ TAB TALONARIOS ═══════════ */}
      {tab === "talonarios" && (
        <div className="space-y-6">
          {/* Formulario crear/editar */}
          {puedeRegistrar && (
            <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-200">
                  {editingCh ? "Editar Talonario" : "Nuevo Talonario"}
                </h2>
                {!showFormCh && (
                  <button
                    onClick={() => { setShowFormCh(true); setEditingCh(null); setFormCh({ cuenta_bancaria_id: "", serie: "", desde: "", hasta: "", fecha_activacion: "" }); }}
                    className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Nuevo talonario
                  </button>
                )}
              </div>

              {showFormCh && (
                <form onSubmit={handleGuardarCh} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Cuenta bancaria *</label>
                      <select
                        value={formCh.cuenta_bancaria_id}
                        onChange={e => setFormCh(p => ({ ...p, cuenta_bancaria_id: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                        required
                      >
                        <option value="">— Seleccionar cuenta —</option>
                        {bancos.map(b => (
                          <option key={b.id} value={b.id}>
                            {b.banco ? `${b.banco} — ` : ""}{b.nombre} ({b.moneda})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Serie *</label>
                      <input
                        value={formCh.serie}
                        onChange={e => setFormCh(p => ({ ...p, serie: e.target.value.toUpperCase() }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                        placeholder="Ej: A"
                        maxLength={5}
                        required
                      />
                    </div>
                    {!editingCh && (
                      <>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Número desde *</label>
                          <input
                            type="number"
                            value={formCh.desde}
                            onChange={e => setFormCh(p => ({ ...p, desde: e.target.value }))}
                            className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                            placeholder="Ej: 155505"
                            min={1}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-400 mb-1">Número hasta *</label>
                          <input
                            type="number"
                            value={formCh.hasta}
                            onChange={e => setFormCh(p => ({ ...p, hasta: e.target.value }))}
                            className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                            placeholder="Ej: 155554"
                            min={1}
                            required
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Fecha de activación *</label>
                      <DateInput
                        value={formCh.fecha_activacion}
                        onChange={e => setFormCh(p => ({ ...p, fecha_activacion: e.target.value }))}
                        className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                        required
                      />
                    </div>
                    {!editingCh && formCh.desde && formCh.hasta && Number(formCh.hasta) >= Number(formCh.desde) && (
                      <div className="flex items-end">
                        <div className="bg-cyan-900/20 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-sm text-cyan-300">
                          Rango: {Number(formCh.desde)} – {Number(formCh.hasta)}
                          <span className="text-cyan-500 ml-2">({Number(formCh.hasta) - Number(formCh.desde) + 1} hojas)</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" disabled={savingCh} className="bg-cyan-600 hover:bg-cyan-700 text-white px-5 py-2 rounded-lg text-sm transition-colors disabled:opacity-50">
                      {savingCh ? "Guardando…" : editingCh ? "Actualizar" : "Crear talonario"}
                    </button>
                    <button type="button" onClick={() => { setShowFormCh(false); setEditingCh(null); }} className="bg-white/5 hover:bg-white/10 text-slate-300 px-5 py-2 rounded-lg text-sm transition-colors">
                      Cancelar
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Tabla de chequeras */}
          <div className="bg-arandu-dark-light border border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-semibold text-slate-200">Talonarios registrados</h3>
              <button onClick={cargarChequeras} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                Actualizar
              </button>
            </div>
            {loadingCh ? (
              <div className="py-12 text-center text-slate-500">Cargando…</div>
            ) : chequeras.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No hay talonarios registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-xs text-slate-400 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Banco / Cuenta</th>
                      <th className="px-4 py-3 text-left">Serie</th>
                      <th className="px-4 py-3 text-left">Rango</th>
                      <th className="px-4 py-3 text-left">Moneda</th>
                      <th className="px-4 py-3 text-left">Activación</th>
                      <th className="px-4 py-3 text-center">Disponibles</th>
                      <th className="px-4 py-3 text-center">Emitidos</th>
                      <th className="px-4 py-3 text-center">Diferidos</th>
                      <th className="px-4 py-3 text-center">Anulados</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {chequeras.map(ch => (
                      <tr key={ch.id} className="hover:bg-white/3 transition-colors">
                        <td className="px-4 py-3 text-white font-medium text-xs">{ch.banco_nombre}</td>
                        <td className="px-4 py-3 text-slate-300 font-mono">{ch.serie}</td>
                        <td className="px-4 py-3 text-slate-400 font-mono text-xs">{ch.desde} – {ch.hasta}</td>
                        <td className="px-4 py-3">
                          <span className="bg-slate-700 text-slate-300 text-xs px-2 py-0.5 rounded">{ch.moneda}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{ch.fecha_activacion}</td>
                        <td className="px-4 py-3 text-center text-slate-300 font-medium">{ch.conteo_disponible}</td>
                        <td className="px-4 py-3 text-center text-emerald-400 font-medium">{ch.conteo_emitido}</td>
                        <td className="px-4 py-3 text-center text-amber-400 font-medium">{ch.conteo_diferido}</td>
                        <td className="px-4 py-3 text-center text-red-400 font-medium">{ch.conteo_anulado}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {puedeEditar && (
                              <button onClick={() => abrirEditar(ch)} className="p-1.5 hover:bg-white/10 rounded text-slate-400 hover:text-white transition-colors" title="Editar">
                                <Edit2 className="w-4 h-4" />
                              </button>
                            )}
                            {puedeEliminar && (
                              <button onClick={() => handleEliminarCh(ch)} className="p-1.5 hover:bg-red-900/30 rounded text-slate-500 hover:text-red-400 transition-colors" title="Eliminar (solo si todas disponibles)">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => { setFiltros(p => ({ ...p, chequera_id: ch.id })); setTab("auditoria"); }}
                              className="p-1.5 hover:bg-cyan-900/30 rounded text-slate-400 hover:text-cyan-400 transition-colors"
                              title="Ver hojas"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ TAB AUDITORÍA ═══════════ */}
      {tab === "auditoria" && (
        <div className="space-y-4">
          {/* Filtros */}
          <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400 font-medium">Filtros</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              <select
                value={filtros.chequera_id}
                onChange={e => setFiltros(p => ({ ...p, chequera_id: e.target.value }))}
                className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
              >
                <option value="">Todos los talonarios</option>
                {chequeras.map(ch => (
                  <option key={ch.id} value={ch.id}>{ch.serie} ({ch.desde}-{ch.hasta}) — {ch.banco_nombre}</option>
                ))}
              </select>
              <select
                value={filtros.estado}
                onChange={e => setFiltros(p => ({ ...p, estado: e.target.value }))}
                className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
              >
                <option value="">Todos los estados</option>
                <option value="disponible">Disponible</option>
                <option value="emitido">Emitido</option>
                <option value="diferido">Diferido</option>
                <option value="descontado">Descontado</option>
                <option value="anulado">Anulado</option>
              </select>
              <select
                value={filtros.cuenta_bancaria_id}
                onChange={e => setFiltros(p => ({ ...p, cuenta_bancaria_id: e.target.value }))}
                className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
              >
                <option value="">Todas las cuentas</option>
                {bancos.map(b => (
                  <option key={b.id} value={b.id}>{b.banco ? `${b.banco} — ` : ""}{b.nombre}</option>
                ))}
              </select>
              <DateInput value={filtros.desde_fecha} onChange={e => setFiltros(p => ({ ...p, desde_fecha: e.target.value }))} className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50" title="F. emisión desde" />
              <DateInput value={filtros.hasta_fecha} onChange={e => setFiltros(p => ({ ...p, hasta_fecha: e.target.value }))} className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50" title="F. emisión hasta" />
            </div>
            <div className="flex gap-3 mt-3 flex-wrap">
              <button
                onClick={() => { setFiltros(p => ({ ...p, desde_fecha: hoyStr(), hasta_fecha: hoyStr() })); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium border transition-all ${filtros.desde_fecha === hoyStr() && filtros.hasta_fecha === hoyStr() ? "bg-emerald-600 border-emerald-600 text-white" : "border-white/10 text-slate-400 hover:text-white bg-white/5"}`}
              >
                Hoy
              </button>
              <button
                onClick={() => { setFiltros(p => ({ ...p, desde_fecha: "", hasta_fecha: "" })); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium border transition-all ${!filtros.desde_fecha && !filtros.hasta_fecha ? "bg-emerald-600 border-emerald-600 text-white" : "border-white/10 text-slate-400 hover:text-white bg-white/5"}`}
              >
                Todos
              </button>
              <button onClick={() => cargarHojas(1)} className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">
                <Search className="w-4 h-4" />Buscar
              </button>
              <button onClick={() => setFiltros({ chequera_id: "", estado: "", cuenta_bancaria_id: "", desde_fecha: hoyStr(), hasta_fecha: hoyStr() })} className="bg-white/5 hover:bg-white/10 text-slate-300 px-4 py-2 rounded-lg text-sm transition-colors">
                Limpiar
              </button>
            </div>
          </div>

          {/* Tabla hojas */}
          <div className="bg-arandu-dark-light border border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-semibold text-slate-200">
                Hojas de cheque
                {totalHojas > 0 && <span className="ml-2 text-sm font-normal text-slate-500">({totalHojas})</span>}
              </h3>
            </div>
            {loadingH ? (
              <div className="py-12 text-center text-slate-500">Cargando…</div>
            ) : hojas.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No se encontraron hojas</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-xs text-slate-400 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Nº Cheque</th>
                      <th className="px-4 py-3 text-left">Banco / Cuenta</th>
                      <th className="px-4 py-3 text-left">Estado</th>
                      <th className="px-4 py-3 text-left">Beneficiario</th>
                      <th className="px-4 py-3 text-right">Valor</th>
                      <th className="px-4 py-3 text-left">F. Emisión</th>
                      <th className="px-4 py-3 text-left">F. Pago / Descuento</th>
                      <th className="px-4 py-3 text-left">Tipo</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {hojas.map(h => (
                      <tr key={h.id} className="hover:bg-white/3 transition-colors">
                        <td className="px-4 py-3 font-mono text-white font-medium text-xs">
                          {h.serie}{String(h.numero).padStart(6, "0")}
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs max-w-[160px] truncate" title={h.banco_nombre}>
                          {h.banco_nombre}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded font-medium ${ESTADO_BADGE[h.estado] || ""}`}>
                            {ESTADO_LABEL[h.estado] || h.estado}
                          </span>
                          {h.estado === "diferido" && h.fecha_pago && (
                            <div className="text-xs text-amber-500 mt-0.5">Vence {h.fecha_pago}</div>
                          )}
                          {h.estado === "descontado" && h.fecha_descuento && (
                            <div className="text-xs text-cyan-500 mt-0.5">Desc. {h.fecha_descuento}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-300 text-xs max-w-[140px] truncate" title={h.nombre_beneficiario}>
                          {h.nombre_beneficiario || <span className="text-slate-600">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-300 text-xs">
                          {h.valor != null ? `${h.moneda === "USD" ? "USD " : h.moneda === "BRL" ? "R$ " : "₲ "}${fmtNum(h.valor)}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{h.fecha_emision || "—"}</td>
                        <td className="px-4 py-3 text-xs">
                          {h.estado === "descontado"
                            ? <span className="text-cyan-400">{h.fecha_descuento || "—"}</span>
                            : <span className="text-slate-400">{h.fecha_pago || "—"}</span>}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {h.tipo_cheque === "diferido" ? <span className="text-amber-400">Diferido</span>
                            : h.tipo_cheque === "al_dia" ? <span className="text-emerald-400">Al día</span>
                            : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {puedeEmitir && h.estado === "disponible" && (
                              <button onClick={() => abrirEmitir(h)} className="flex items-center gap-1 bg-emerald-900/30 hover:bg-emerald-900/60 text-emerald-400 px-2 py-1.5 rounded text-xs transition-colors">
                                <FileText className="w-3.5 h-3.5" />Emitir
                              </button>
                            )}
                            {puedeEmitir && !puedeLiberar && (h.estado === "emitido" || h.estado === "diferido") && !h.egreso_id && (
                              <button onClick={() => liberarHoja(h)} className="flex items-center gap-1 bg-slate-700/40 hover:bg-slate-700/70 text-slate-300 px-2 py-1.5 rounded text-xs transition-colors" title="Cheque emitido sin pago vinculado que no se entregó: vuelve a disponible">
                                <FileText className="w-3.5 h-3.5" />Liberar
                              </button>
                            )}
                            {puedeLiberar && (h.estado === "descontado" || h.estado === "emitido" || h.estado === "diferido") && (
                              <button onClick={() => liberarChequeUsado(h)} className="flex items-center gap-1 bg-violet-900/30 hover:bg-violet-900/60 text-violet-300 px-2 py-1.5 rounded text-xs transition-colors" title="Libera el cheque y lo desvincula del pago, para poder anular el pago o pagar de nuevo">
                                <FileText className="w-3.5 h-3.5" />Liberar
                              </button>
                            )}
                            {puedeDescontar && h.estado === "diferido" && (
                              <button onClick={() => abrirDescontar(h)} className="flex items-center gap-1 bg-cyan-900/30 hover:bg-cyan-900/60 text-cyan-400 px-2 py-1.5 rounded text-xs transition-colors">
                                <CheckCircle className="w-3.5 h-3.5" />Descontar
                              </button>
                            )}
                            {puedeAnular && (h.estado === "disponible" || h.estado === "emitido" || h.estado === "diferido") && (
                              <button onClick={() => { setHojaAnulando(h); setMotivoAnulacion(""); }} className="flex items-center gap-1 bg-red-900/30 hover:bg-red-900/60 text-red-400 px-2 py-1.5 rounded text-xs transition-colors" title={h.estado === "disponible" ? "Inactivar/anular esta hoja sin emitirla" : "Anular"}>
                                <XCircle className="w-3.5 h-3.5" />{h.estado === "disponible" ? "Inactivar" : "Anular"}
                              </button>
                            )}
                            {(h.estado === "emitido" || h.estado === "diferido" || h.estado === "descontado" || h.estado === "anulado") && (
                              <button onClick={() => imprimirCheque(h)} className="p-1.5 hover:bg-white/10 rounded text-slate-400 hover:text-white transition-colors" title="Imprimir">
                                <Printer className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {pagesHojas > 1 && (
              <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between">
                <span className="text-xs text-slate-500">Página {pageHojas} de {pagesHojas}</span>
                <div className="flex gap-2">
                  <button onClick={() => cargarHojas(pageHojas - 1)} disabled={pageHojas <= 1} className="p-2 hover:bg-white/10 rounded-lg disabled:opacity-40 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => cargarHojas(pageHojas + 1)} disabled={pageHojas >= pagesHojas} className="p-2 hover:bg-white/10 rounded-lg disabled:opacity-40 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ TAB PROYECCIÓN ═══════════ */}
      {tab === "proyeccion" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white">Proyección de cheques diferidos</h3>
              <p className="text-xs text-slate-500 mt-0.5">Cheques pendientes de descuento ordenados por fecha de pago</p>
            </div>
            <button onClick={cargarProyeccion} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg transition-colors">
              <TrendingDown className="w-3.5 h-3.5" /> Actualizar
            </button>
          </div>

          {loadingProy ? (
            <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" /> Cargando proyección...
            </div>
          ) : !proyData || proyData.total === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              No hay cheques diferidos pendientes de descuento.
            </div>
          ) : (() => {
            const { diferidos, cuentas } = proyData;
            // Agrupar por cuenta bancaria para mostrar saldo proyectado
            const porCuenta = {};
            diferidos.forEach(h => {
              const cid = h.cuenta_bancaria_id || "sin_cuenta";
              if (!porCuenta[cid]) porCuenta[cid] = { nombre: h.banco_nombre || "Banco", moneda: cuentas[cid]?.moneda || "PYG", saldoActual: cuentas[cid]?.saldo ?? null, cheques: [] };
              porCuenta[cid].cheques.push(h);
            });

            return (
              <div className="space-y-5">
                {Object.entries(porCuenta).map(([cid, grupo]) => {
                  // Calcular saldo acumulado restando cada cheque
                  let saldoAcum = grupo.saldoActual;
                  const chequesConSaldo = grupo.cheques.map(h => {
                    const saldoDespues = saldoAcum !== null ? saldoAcum - h.valor : null;
                    saldoAcum = saldoDespues;
                    return { ...h, saldo_proyectado: saldoDespues };
                  });

                  const fmtM = (n, m) => {
                    if (n == null) return "—";
                    if (m === "USD") return `USD ${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    if (m === "BRL") return `R$ ${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    return `₲ ${Math.round(n).toLocaleString("es-PY")}`;
                  };

                  return (
                    <div key={cid} className="bg-arandu-dark-light border border-white/10 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-cyan-400" />
                          <span className="font-medium text-sm">{grupo.nombre}</span>
                          <span className="text-xs text-slate-500">({grupo.moneda})</span>
                        </div>
                        {grupo.saldoActual !== null && (
                          <div className="text-right text-xs">
                            <span className="text-slate-400">Saldo actual: </span>
                            <span className={`font-semibold ${grupo.saldoActual >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              {fmtM(grupo.saldoActual, grupo.moneda)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/5 text-xs text-slate-500 uppercase">
                              <th className="px-4 py-2.5 text-left">Fecha pago</th>
                              <th className="px-4 py-2.5 text-left">Cheque</th>
                              <th className="px-4 py-2.5 text-left">Beneficiario</th>
                              <th className="px-4 py-2.5 text-right">Valor</th>
                              {grupo.saldoActual !== null && <th className="px-4 py-2.5 text-right">Saldo proyectado</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {chequesConSaldo.map(h => (
                              <tr key={h.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                                <td className="px-4 py-3 text-slate-300 font-mono text-xs">{h.fecha_pago}</td>
                                <td className="px-4 py-3">
                                  <span className="font-mono text-amber-300 text-xs">{h.serie}{String(h.numero).padStart(6, "0")}</span>
                                </td>
                                <td className="px-4 py-3 text-slate-300 text-xs">{h.nombre_beneficiario || "—"}</td>
                                <td className="px-4 py-3 text-right font-semibold text-red-400 text-xs">{fmtM(h.valor, h.moneda)}</td>
                                {grupo.saldoActual !== null && (
                                  <td className={`px-4 py-3 text-right text-xs font-semibold ${(h.saldo_proyectado ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                                    {fmtM(h.saldo_proyectado, grupo.moneda)}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-white/10 bg-white/3">
                              <td colSpan={3} className="px-4 py-2.5 text-xs text-slate-400 font-medium">
                                Total a descontar ({grupo.cheques.length} cheque{grupo.cheques.length !== 1 ? "s" : ""})
                              </td>
                              <td className="px-4 py-2.5 text-right text-xs font-bold text-red-400">
                                {fmtM(grupo.cheques.reduce((s, h) => s + (h.valor || 0), 0), grupo.moneda)}
                              </td>
                              {grupo.saldoActual !== null && <td />}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* ═══════════ TAB CONFIGURACIÓN (medidas de impresión) ═══════════ */}
      {tab === "configuracion" && puedeConfigurarImpresion && (
        <div className="space-y-6">
          <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-5 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-200">Configuración de impresión</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Ajustá la posición (en milímetros) de cada campo hasta que calce con tu talonario físico. La vista previa está a escala real.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-400 whitespace-nowrap">Aplicar a:</label>
                <select
                  value={configScope}
                  onChange={e => setConfigScope(e.target.value)}
                  className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                >
                  <option value="general">General (todas las cuentas)</option>
                  {bancos.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.banco ? `${b.banco} — ` : ""}{b.nombre} ({b.moneda}){configList.some(c => c.cuenta_bancaria_id === b.id) ? " · personalizado" : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {loadingConfig ? (
              <div className="py-10 text-center text-slate-500 text-sm">Cargando…</div>
            ) : (
              <>
                {configScope !== "general" && !configList.some(c => (c.cuenta_bancaria_id || null) === configScope) && (
                  <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    Esta cuenta todavía no tiene una configuración propia — se está mostrando (y editando a partir de) la configuración general.
                  </p>
                )}

                {/* ── CHEQUE VISTA ── */}
                <div className="border-t border-white/10 pt-5">
                  <h3 className="text-sm font-semibold text-slate-300 mb-3">Cheque Vista (al día)</h3>
                  <div className="grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-6">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        ["monto_top", "Monto — top"], ["monto_right", "Monto — right"],
                        ["fecha_top", "Fecha — top"], ["fecha_right", "Fecha — right"],
                        ["benef_top", "Beneficiario — top"], ["benef_left", "Beneficiario — left"],
                        ["letras_top", "Monto en letras — top"], ["letras_left", "Monto en letras — left"],
                        ["concepto_top", "Concepto — top"], ["concepto_left", "Concepto — left"],
                      ].map(([campo, label]) => (
                        <div key={campo}>
                          <label className="block text-[11px] text-slate-400 mb-1">{label}</label>
                          <div className="relative">
                            <input
                              type="number" step="0.5"
                              value={configForms.vista[campo] ?? ""}
                              onChange={e => setCampoImpresion("vista", campo, e.target.value)}
                              className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-2.5 pr-7 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">mm</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="relative bg-white rounded shadow-inner border border-slate-300" style={{ width: "175mm", height: "76mm" }}>
                        <div className="absolute font-bold" style={{ top: `${configForms.vista.monto_top || 0}mm`, right: `${configForms.vista.monto_right || 0}mm`, fontSize: "9pt", fontFamily: "'Courier New', monospace", color: "#111" }}>₲ 1.234.567</div>
                        <div className="absolute font-bold" style={{ top: `${configForms.vista.fecha_top || 0}mm`, right: `${configForms.vista.fecha_right || 0}mm`, fontSize: "9pt", fontFamily: "'Courier New', monospace", color: "#111" }}>05 - 09 - 2026</div>
                        <div className="absolute font-bold whitespace-nowrap" style={{ top: `${configForms.vista.benef_top || 0}mm`, left: `${configForms.vista.benef_left || 0}mm`, fontSize: "9pt", color: "#111" }}>JUAN PÉREZ EJEMPLO</div>
                        <div className="absolute font-bold whitespace-nowrap" style={{ top: `${configForms.vista.letras_top || 0}mm`, left: `${configForms.vista.letras_left || 0}mm`, fontSize: "8pt", color: "#111" }}>₲ 1.234.567 &nbsp; UN MILLÓN DOSCIENTOS...</div>
                        <div className="absolute" style={{ top: `${configForms.vista.concepto_top || 0}mm`, left: `${configForms.vista.concepto_left || 0}mm`, fontSize: "7.5pt", color: "#444" }}>Concepto de ejemplo</div>
                      </div>
                      <p className="text-[10px] text-slate-500">Vista previa a escala real (175×76mm)</p>
                    </div>
                  </div>
                </div>

                {/* ── CHEQUE DIFERIDO ── */}
                <div className="border-t border-white/10 pt-5">
                  <h3 className="text-sm font-semibold text-slate-300 mb-3">Cheque Diferido</h3>
                  <div className="grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-6">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        ["fecha_emision_top", "Fecha emisión — top"], ["fecha_emision_left", "Fecha emisión — left"],
                        ["fecha_pago_top", "Fecha pago — top"], ["fecha_pago_right", "Fecha pago — right"],
                        ["benef_top", "Beneficiario — top"], ["benef_left", "Beneficiario — left"],
                        ["letras_top", "Monto en letras — top"], ["letras_left", "Monto en letras — left"],
                        ["concepto_top", "Concepto — top"], ["concepto_left", "Concepto — left"],
                      ].map(([campo, label]) => (
                        <div key={campo}>
                          <label className="block text-[11px] text-slate-400 mb-1">{label}</label>
                          <div className="relative">
                            <input
                              type="number" step="0.5"
                              value={configForms.diferido[campo] ?? ""}
                              onChange={e => setCampoImpresion("diferido", campo, e.target.value)}
                              className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-2.5 pr-7 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500">mm</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="relative bg-white rounded shadow-inner border border-slate-300" style={{ width: "175mm", height: "76mm" }}>
                        <div className="absolute font-bold" style={{ top: `${configForms.diferido.fecha_emision_top || 0}mm`, left: `${configForms.diferido.fecha_emision_left || 0}mm`, fontSize: "9pt", fontFamily: "'Courier New', monospace", color: "#111" }}>05 - 09 - 2026</div>
                        <div className="absolute font-bold whitespace-nowrap" style={{ top: `${configForms.diferido.fecha_pago_top || 0}mm`, right: `${configForms.diferido.fecha_pago_right || 0}mm`, fontSize: "8pt", color: "#111" }}>Fecha de pago: 05 - 10 - 2026</div>
                        <div className="absolute font-bold whitespace-nowrap" style={{ top: `${configForms.diferido.benef_top || 0}mm`, left: `${configForms.diferido.benef_left || 0}mm`, fontSize: "9pt", color: "#111" }}>JUAN PÉREZ EJEMPLO</div>
                        <div className="absolute font-bold whitespace-nowrap" style={{ top: `${configForms.diferido.letras_top || 0}mm`, left: `${configForms.diferido.letras_left || 0}mm`, fontSize: "8pt", color: "#111" }}>₲ 1.234.567 &nbsp; UN MILLÓN DOSCIENTOS...</div>
                        <div className="absolute" style={{ top: `${configForms.diferido.concepto_top || 0}mm`, left: `${configForms.diferido.concepto_left || 0}mm`, fontSize: "7.5pt", color: "#444" }}>Concepto de ejemplo</div>
                      </div>
                      <p className="text-[10px] text-slate-500">Vista previa a escala real (175×76mm)</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-white/10 pt-4">
                  <button
                    onClick={handleRestaurarConfigImpresion}
                    className="px-4 py-2 rounded-lg text-sm text-slate-300 bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    Restaurar valores por defecto
                  </button>
                  <button
                    onClick={handleGuardarConfigImpresion}
                    disabled={savingConfig}
                    className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 transition-colors"
                  >
                    {savingConfig ? "Guardando…" : "Guardar configuración"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ── Modales (compartidos entre standalone y embedded) ─────────────────────
  const modales = (
    <>
      {/* ── Aviso: diferidos que llegaron a su fecha ── */}
      {avisoDiferidosAbierto && diferidosVencidos.length > 0 && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-amber-500/30 rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="px-6 py-5 border-b border-white/10">
              <h3 className="font-heading font-semibold text-amber-300">Cheques diferidos para descontar</h3>
              <p className="text-xs text-slate-400 mt-1">Llegó la fecha de pago de estos cheques. Registrá el descuento cuando el banco los debite.</p>
            </div>
            <div className="p-4 max-h-72 overflow-y-auto space-y-2">
              {diferidosVencidos.map(h => (
                <div key={h.id} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-3 py-2 text-xs">
                  <div>
                    <p className="text-white font-medium">{h.serie}{String(h.numero).padStart(6, "0")} — {h.nombre_beneficiario}</p>
                    <p className="text-slate-400">{h.banco_nombre} · {h.moneda} {Number(h.valor || 0).toLocaleString("es-PY")} · vence {h.fecha_pago}</p>
                  </div>
                  {puedeDescontar && (
                    <button onClick={() => { setAvisoDiferidosAbierto(false); abrirDescontar(h); }}
                      className="shrink-0 flex items-center gap-1 bg-cyan-900/30 hover:bg-cyan-900/60 text-cyan-400 px-2 py-1.5 rounded transition-colors">
                      <CheckCircle className="w-3.5 h-3.5" />Descontar
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="px-6 pb-5">
              <button onClick={() => setAvisoDiferidosAbierto(false)} className="w-full bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors">Recordármelo después</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal EMITIR ── */}
      {hojaEmitiendo && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-emerald-400 shrink-0" />
                <div>
                  <h3 className="font-heading font-semibold">Emitir Cheque</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {hojaEmitiendo.serie}{String(hojaEmitiendo.numero).padStart(6,"0")} &nbsp;·&nbsp; {hojaEmitiendo.banco_nombre}
                  </p>
                </div>
              </div>
              <button onClick={() => setHojaEmitiendo(null)} className="p-1 rounded hover:bg-white/10 text-slate-400">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleEmitir} className="p-6 space-y-4">
              {comprasPend.length > 0 && (
                <div className="border border-emerald-500/20 bg-emerald-500/5 rounded-lg p-3 space-y-2">
                  <label className="block text-xs text-emerald-300 font-medium">Pagar compra pendiente con este cheque (opcional)</label>
                  <select value={compraVincId} onChange={e => elegirCompraVinc(e.target.value)} style={{ colorScheme: "dark" }}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/50">
                    <option value="">— Cheque suelto (sin vincular) —</option>
                    {comprasPend.map(c => (
                      <option key={c.id} value={c.id}>{c.proveedor_nombre} · {c.numero || c.fecha} · saldo {Number(c.saldo_pendiente).toLocaleString("es-PY")} {c.moneda || "PYG"}</option>
                    ))}
                  </select>
                  {compraVinc && (
                    <p className="text-[11px] text-slate-400">
                      Al emitir, la compra queda con este pago{formEmitir.fecha_pago > formEmitir.fecha_emision ? " y el banco se debita recién en la fecha de pago del cheque" : ""}.
                    </p>
                  )}
                  {compraVinc && (compraVinc.moneda || "PYG") !== (hojaEmitiendo?.moneda || "PYG") && (
                    <input type="number" step="any" min="0" value={tcEmitir} onChange={e => setTcEmitir(e.target.value)}
                      placeholder={`Tipo de cambio (compra en ${compraVinc.moneda}, cuenta en ${hojaEmitiendo?.moneda || "PYG"})`}
                      className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/50" />
                  )}
                </div>
              )}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Beneficiario *</label>
                <input
                  value={formEmitir.nombre_beneficiario}
                  onChange={e => setFormEmitir(p => ({ ...p, nombre_beneficiario: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50"
                  placeholder="Nombre o razón social"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">RUC / CI</label>
                <input
                  value={formEmitir.ruc_beneficiario}
                  onChange={e => setFormEmitir(p => ({ ...p, ruc_beneficiario: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50"
                  placeholder="Opcional"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Valor ({hojaEmitiendo.moneda}) *</label>
                <input
                  type="number"
                  value={formEmitir.valor}
                  onChange={e => setFormEmitir(p => ({ ...p, valor: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50"
                  placeholder="0"
                  min={0.01}
                  step="any"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Fecha emisión *</label>
                  <DateInput
                    value={formEmitir.fecha_emision}
                    onChange={e => setFormEmitir(p => ({ ...p, fecha_emision: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Fecha pago *</label>
                  <DateInput
                    value={formEmitir.fecha_pago}
                    onChange={e => setFormEmitir(p => ({ ...p, fecha_pago: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50"
                    required
                  />
                </div>
              </div>
              {formEmitir.fecha_pago && formEmitir.fecha_emision && (
                <div className={`text-xs px-3 py-2 rounded-lg ${formEmitir.fecha_pago > formEmitir.fecha_emision ? "bg-amber-900/20 text-amber-300" : "bg-emerald-900/20 text-emerald-300"}`}>
                  {formEmitir.fecha_pago > formEmitir.fecha_emision ? `⚠ Cheque diferido — pagadero el ${formEmitir.fecha_pago}` : "✓ Cheque al día"}
                </div>
              )}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Concepto</label>
                <input
                  value={formEmitir.concepto}
                  onChange={e => setFormEmitir(p => ({ ...p, concepto: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50"
                  placeholder="Descripción opcional"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={savingEmitir} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                  {savingEmitir ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Emitiendo…</span> : "Emitir cheque"}
                </button>
                <button type="button" onClick={() => setHojaEmitiendo(null)} className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {hojaAnulando && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <h3 className="font-heading font-semibold">{hojaAnulando.estado === "disponible" ? "Inactivar hoja de cheque" : "Anular Cheque"}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {hojaAnulando.serie}{String(hojaAnulando.numero).padStart(6, "0")}
                  {hojaAnulando.nombre_beneficiario ? ` — ${hojaAnulando.nombre_beneficiario}` : " — hoja sin emitir"}
                </p>
              </div>
            </div>
            <form onSubmit={handleAnular} className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Motivo * <span className="text-slate-600">(mínimo 6 caracteres)</span></label>
                <textarea value={motivoAnulacion} onChange={e => setMotivoAnulacion(e.target.value)} className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-red-500/50 resize-none" rows={3} placeholder="Descripción del motivo de anulación" required minLength={6} />
                <p className="text-xs text-slate-600 mt-1">{motivoAnulacion.length} caracteres</p>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={savingAnular || motivoAnulacion.trim().length < 6} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                  {savingAnular ? "Anulando…" : "Confirmar anulación"}
                </button>
                <button type="button" onClick={() => { setHojaAnulando(null); setMotivoAnulacion(""); }} className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {hojaDescontando && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-cyan-400 shrink-0" />
              <div>
                <h3 className="font-heading font-semibold">Registrar descuento de cheque</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {hojaDescontando.serie}{String(hojaDescontando.numero).padStart(6, "0")} — {hojaDescontando.nombre_beneficiario}
                  {hojaDescontando.fecha_pago && <span className="ml-1 text-amber-400">(Vencía: {hojaDescontando.fecha_pago})</span>}
                </p>
              </div>
            </div>
            <form onSubmit={handleDescontar} className="p-6 space-y-4">
              <div className="bg-cyan-900/10 border border-cyan-500/20 rounded-lg px-4 py-3 text-xs text-cyan-300">
                Al descontar el cheque, se registrará en el reporte Caja/Banco en la fecha de descuento que indiques.
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Fecha de descuento *</label>
                <DateInput
                  value={fechaDescuento}
                  onChange={e => setFechaDescuento(e.target.value)}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Fecha real en que el banco acreditó o descontó el cheque</p>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={savingDescontar || !fechaDescuento} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                  {savingDescontar ? "Guardando…" : "Confirmar descuento"}
                </button>
                <button type="button" onClick={() => setHojaDescontando(null)} className="flex-1 bg-white/5 hover:bg-white/10 text-slate-300 py-2.5 rounded-lg text-sm transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  // ── RENDER: embedded (dentro de FinancieroPage) ───────────────────────────
  if (embedded) {
    return (
      <div className="text-white">
        {tabsNav}
        {content}
        {modales}
      </div>
    );
  }

  // ── RENDER: standalone (ruta /sistema/cheques) ────────────────────────────
  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      <div className="border-b border-white/10 bg-arandu-dark-light/50 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-500/15 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-lg">Emisión de Cheques</h1>
            <p className="text-xs text-slate-500">Talonarios y auditoría de hojas</p>
          </div>
        </div>
      </div>
      {tabsNav}
      {content}
      {modales}
    </div>
  );
}
